import { useEffect, useMemo, useState } from "react";

import { ArrowLeft, Plus, Search, Upload } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  useAccountMappings,
  useCreateAccountMapping,
  useDeleteAccountMapping,
  useUpdateAccountMapping,
} from "~/hooks/contabilidad/useAccountMappings";
import { useMappingTemplate } from "~/hooks/contabilidad/useMappingTemplates";
import type {
  AccountMapping,
  AccountMappingFormData,
  ClaseType,
  ReporteType,
} from "~/types/contabilidad";
import { CLASE_OPTIONS, REPORTE_OPTIONS } from "~/types/contabilidad";

import { MapeoEditDialog } from "./components/MapeoEditDialog";
import { MapeoImportDialog } from "./components/MapeoImportDialog";
import { MapeoTable } from "./components/MapeoTable";

export default function MapeoDetailPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [reporteFilter, setReporteFilter] = useState<string>("all");
  const [claseFilter, setClaseFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<
    AccountMapping | undefined
  >(undefined);

  const { data: template, isLoading: isLoadingTemplate } = useMappingTemplate(
    templateId ?? ""
  );
  const { data: mappings = [], isLoading: isLoadingMappings } =
    useAccountMappings({
      template_id: templateId,
    });

  const createMutation = useCreateAccountMapping();
  const updateMutation = useUpdateAccountMapping();
  const deleteMutation = useDeleteAccountMapping();

  const filteredMappings = useMemo(() => {
    return mappings.filter((mapping) => {
      const matchesSearch =
        !searchQuery ||
        mapping.account?.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        mapping.account?.name.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesReporte =
        reporteFilter === "all" || mapping.reporte === reporteFilter;

      const matchesClase =
        claseFilter === "all" || mapping.clase === claseFilter;

      return matchesSearch && matchesReporte && matchesClase;
    });
  }, [mappings, searchQuery, reporteFilter, claseFilter]);

  const handleCreate = () => {
    setEditingMapping(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (mapping: AccountMapping) => {
    setEditingMapping(mapping);
    setDialogOpen(true);
  };

  const handleSubmit = (data: AccountMappingFormData) => {
    if (editingMapping) {
      updateMutation.mutate(
        { id: editingMapping.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingMapping(undefined);
          },
        }
      );
    } else {
      createMutation.mutate(
        { ...data, template_id: templateId! },
        {
          onSuccess: () => {
            setDialogOpen(false);
          },
        }
      );
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  if (isLoadingTemplate) {
    return (
      <div className="container mx-auto w-[80vw] space-y-6 py-6">
        <Skeleton className="h-8 w-[200px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container mx-auto w-[80vw] py-6">
        <p className="text-muted-foreground">Plantilla no encontrada.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/contabilidad/configuracion/mapeo">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{template.name}</h1>
              <Badge
                variant={template.is_active ? "default" : "secondary"}
                className={
                  template.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
                }
              >
                {template.is_active ? "Activa" : "Inactiva"}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {template.description || "Sin descripcion"} |{" "}
              {template.company?.name}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setImportDialogOpen(true)}
            className="rounded-full"
          >
            <Upload className="mr-2 h-4 w-4" />
            Importar
          </Button>
          <Button onClick={handleCreate} className="rounded-full">
            <Plus className="mr-2 h-4 w-4" />
            Agregar Mapeo
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4 pt-4">
            <div className="relative min-w-[200px] max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por codigo o nombre de cuenta..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 rounded-xl pl-9"
              />
            </div>
            <Select value={reporteFilter} onValueChange={setReporteFilter}>
              <SelectTrigger className="h-9 w-[150px] rounded-xl">
                <SelectValue placeholder="Reporte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {REPORTE_OPTIONS.map((reporte) => (
                  <SelectItem key={reporte} value={reporte}>
                    {reporte}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={claseFilter} onValueChange={setClaseFilter}>
              <SelectTrigger className="h-9 w-[180px] rounded-xl">
                <SelectValue placeholder="Clase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las clases</SelectItem>
                {CLASE_OPTIONS.map((clase) => (
                  <SelectItem key={clase} value={clase}>
                    {clase}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground">
              {filteredMappings.length} de {mappings.length} mapeos
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <MapeoTable
            mappings={filteredMappings}
            isLoading={isLoadingMappings}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <MapeoEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mapping={editingMapping}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        templateId={templateId!}
        companyId={template.company_id}
      />

      <MapeoImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        templateId={templateId!}
        templateName={template.name}
        companyId={template.company_id}
        onSuccess={() => {
          // The query will automatically refetch
        }}
      />
    </div>
  );
}
